import { useCallback, useEffect, useRef, useState } from 'react';

import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIn, useFormik } from 'formik';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { cast, handleApiError, notify, catchError } from '~/utils/helper';
import { DeleteCircle, EqualIcon, MultipleIcon, PlusIcon, TrashIcon, ArrowLeftDoubleIcon } from '~/assets/icons';
import { jobActions } from '~/thunks/job/jobSlice';
import { IAreaScope, IAreaScopeCustomField, IEditAreaScope } from '~/utils/interface/areaScope';
import {
  handleCreateAreaScope,
  handleEditAreaScope,
  handleGetAreaScope,
  handleGetAreaScopeList,
} from '~/thunks/areaScope/areaScopeThunk';
import { BaseDatePicker, BaseRow, BaseSelect, BaseTimePicker } from '~/components/common';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { DISPOSE, LOCATION, CLEAN, FLOOR_CUTS } from '~/utils/constants/areaScope';
import { handleGetMasterData } from '~/thunks/master/masterThunk';
import { MASTER_DATA_EQUIPMENTS, MASTER_DATA_MATERIAL_TYPES } from '~/utils/constants/master';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { areaScopeActions } from '~/thunks/areaScope/areaScopeSlice';
import areaScopeApi from '~/apis/areaScope';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { IPayloadDelete } from '~/utils/interface/customer';
import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';
import ModalAddRoom from './Modal/ModalAddRoom';
import { handleCreateMaterialTypes } from '~/thunks/materialType/materialTypeThunk';
import { IMaterialType } from '~/utils/interface/material-type';
import { NumericFormat } from 'react-number-format';
const initialValues: IEditAreaScope = {
  name: '',
  detail: {
    drybook: [],
    doorsDetached: {
      dispose: 'yes',
    },
  },
};

const AreaScopeDetail = () => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'admin/common']);
  const dispatch = useAppDispatch();
  const { id, areaId } = useParams();
  const permissions = usePermission();
  const [openAddRoom, setOpenAddRoom] = useState<boolean>(false);
  const modalActionRef = useRef<((roomId: string, roomName: string) => void) | null>(null);
  const navigate = useNavigate();
  const [editData, setEditData] = useState<IAreaScope>();
  const [modalType, setModalType] = useState<'affected' | 'dh'>('affected'); // Default to 'affected'
  const [roomArea, setRoomArea] = useState<string>('');
  const { openAlertDelete, areaScopes } = useAppSelector((state) => state.areaScope);
  const { equipments, materialTypes } = useAppSelector((state) => state.master);
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      const submitValues = { ...values };

      // Collect new material types from the form
      const newMaterialTypes: any = [];
      submitValues.detail.drybook?.forEach((drybook: any) => {
        const materialType = drybook.materialType;

        // Check if the materialType is new
        const isNewType = !materialTypes?.some((type) => type.name === materialType);
        if (isNewType && materialType) {
          newMaterialTypes.push(materialType);
        }
      });

      try {
        // Save new material types if any
        if (newMaterialTypes.length > 0) {
          for (const materialTypeName of newMaterialTypes) {
            const materialType: IMaterialType = { name: materialTypeName };
            try {
              await dispatch(handleCreateMaterialTypes(materialType)).unwrap();
              notify('success', t('createSuccess', { ns: 'admin/common' }));
            } catch (e: unknown) {
              handleApiError(e, setFieldError);
            }
          }
        }

        // Proceed with the main form submission
        if (areaId === 'create') {
          await dispatch(handleCreateAreaScope(submitValues)).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
          if (id) {
            navigate(`${adminRouteAbsolute.jobDetail.replace(':id', id)}#areaScope`);
          }
        } else if (areaId) {
          await dispatch(
            handleEditAreaScope({
              id: areaId,
              params: submitValues,
            })
          ).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          dispatch(jobActions.changeNeedRefreshData(true));
          if (id) {
            navigate(`${adminRouteAbsolute.jobDetail.replace(':id', id)}#areaScope`);
          }
        }
      } catch (e: unknown) {
        handleApiError(e, setFieldError);
      }
    },
  });

  //#endregion Declare Hook
  //#region Selector

  //#endregion Selector

  //#region Declare State

  //#endregion Declare State
  useEffect(() => {
    if (id) {
      dispatch(handleGetAreaScopeList({ jobId: id }))
        .unwrap()
        .catch(console.error);
    }
  }, [dispatch, id]);

  useEffect(() => {
    form.setFieldValue('jobId', id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (editData) {
      form.setFieldValue('name', cast(editData.name));
      form.setFieldValue('detail', cast(editData.detail));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData]);

  useEffect(() => {
    const fetchAreaScope = async () => {
      if (areaId && areaId !== 'create') {
        try {
          const areaScope = await dispatch(handleGetAreaScope(areaId)).unwrap();
          setEditData(areaScope);
        } catch (error) {
          console.error(error);
        }
      }
    };

    fetchAreaScope();
  }, [dispatch, areaId]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = () => {
    form.resetForm();
    if (id) {
      navigate(`${adminRouteAbsolute.jobDetail.replace(':id', id)}#areaScope`);
    }
  };
  useEffect(() => {
    // Calculate the total whenever length, width, or height changes
    const length = Number(form.values?.detail?.length) || 0;
    const width = Number(form.values?.detail?.width) || 0;
    const height = Number(form.values?.detail?.height) || 0;
    const total = length * width * height;
    const multiplication = length * width;
    // Update the total in the form values
    form.setFieldValue('detail.total', total);
    form.setFieldValue('detail.multiplication', multiplication);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values?.detail?.length, form.values?.detail?.width, form.values?.detail?.height]);

  useEffect(() => {
    // Calculate the total whenever length, width, or height changes
    const length = Number(form.values?.detail?.drywall?.length) || 0;
    const width = Number(form.values?.detail?.drywall?.width) || 0;
    const height = Number(form.values?.detail?.drywall?.height) || 0;
    const total = length * width * height;

    // Update the total in the form values
    form.setFieldValue('detail.drywall.total', total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values?.detail?.drywall?.length, form.values?.detail?.drywall?.width, form.values?.detail?.drywall?.height]);

  useEffect(() => {
    dispatch(
      handleGetMasterData({
        type: [MASTER_DATA_EQUIPMENTS, MASTER_DATA_MATERIAL_TYPES],
      })
    );
  }, [dispatch]);
  const addNewExtractionItem = () => {
    form.setFieldValue('detail.extraction', [
      ...(form.values.detail.extraction ?? []),
      {
        hardSurface: undefined,
        carpet: undefined,
        bulk: undefined,
        weight: undefined,
        heavy: undefined,
        customFields: [],
      },
    ]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addNewOutsideItem = () => {
    form.setFieldValue('detail.atmosphericReadings.outside', [
      ...(form.values.detail.atmosphericReadings?.outside ?? []),
      {
        date: undefined,
        time: undefined,
        temp: undefined,
        rh: undefined,
        gpp: undefined,
      },
    ]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addNewDryBookField = () => {
    const currentDate = new Date();
    const formattedDate = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate
      .getDate()
      .toString()
      .padStart(2, '0')}/${currentDate.getFullYear()}`;

    const newField = {
      area: undefined,
      location: undefined,
      moisturePoint: undefined,
      dryStandard: undefined,
      materialType: undefined,
      goal: undefined,
      readings: [
        {
          date: formattedDate,
          mp: undefined,
          area: undefined,
          mt: undefined,
          location: undefined,
          value: undefined,
          goal: undefined,
        },
      ],
    };

    form.setFieldValue('detail.drybook', [...(form.values.detail.drybook ?? []), newField]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addNewDryBookItem = (index: number) => {
    const newDryBookFields = [...form.values.detail.drybook];
    const currentDate = new Date();
    const formattedDate = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate
      .getDate()
      .toString()
      .padStart(2, '0')}/${currentDate.getFullYear()}`;

    newDryBookFields[index].readings.push({
      date: formattedDate,
      mp: form.values.detail.drybook[index].readings[0].mp,
      area: form.values.detail.drybook[index].readings[0].area,
      mt: form.values.detail.drybook[index].readings[0].mt,
      location: form.values.detail.drybook[index].readings[0].location,
      value: '',
      goal: form.values.detail.drybook[index].readings[0].goal,
    });
    form.setFieldValue('detail.drybook', newDryBookFields);
  };

  const addNewExtractionCustomField = (index: number) => {
    const newExtractionCustomField = [...form.values.detail.extraction];
    newExtractionCustomField[index].customFields.push({
      label: '',
      value: '',
    });
    form.setFieldValue('detail.extraction', newExtractionCustomField);
  };

  const handleDeleteExtractionCustomField = (idx: number, index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.extraction?.[index]?.customFields;

    // If extraction custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields = customFields.filter((_: IAreaScopeCustomField, i: number) => i !== idx);
    form.setFieldValue(`detail.extraction[${index}].customFields`, updatedCustomFields);
  };

  const addNewCleanCustomField = () => {
    let cleanCustomFields: Array<IAreaScopeCustomField> | undefined = form?.values?.detail?.cleanCustomFields;
    if (cleanCustomFields) {
      const newCleanCustomField: Array<IAreaScopeCustomField> = [...form?.values.detail?.cleanCustomFields];
      newCleanCustomField.push({
        label: '',
        value: '',
      });
      form.setFieldValue('detail.cleanCustomFields', newCleanCustomField);
    } else {
      form.setFieldValue('detail.cleanCustomFields', [{ label: '', value: '' }]);
    }
  };

  const handleDeleteCleanCustomField = (index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.cleanCustomFields;

    // If clean custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields: Array<IAreaScopeCustomField> = customFields.filter(
      (_: IAreaScopeCustomField, i: number) => i !== index
    );
    form.setFieldValue(`detail.cleanCustomFields`, updatedCustomFields);
  };

  const addNewContentMovedCustomField = () => {
    let contentsMovedCustomFields: Array<IAreaScopeCustomField> | undefined =
      form?.values?.detail?.contentsMovedCustomFields;
    if (contentsMovedCustomFields) {
      const newContentMovedCustomField: Array<IAreaScopeCustomField> = [
        ...form?.values.detail?.contentsMovedCustomFields,
      ];
      newContentMovedCustomField.push({
        label: '',
        value: '',
      });
      form.setFieldValue('detail.contentsMovedCustomFields', newContentMovedCustomField);
    } else {
      form.setFieldValue('detail.contentsMovedCustomFields', [{ label: '', value: '' }]);
    }
  };

  const handleDeleteCotentMovedCustomField = (index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.contentsMovedCustomFields;

    // If content moved custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields: Array<IAreaScopeCustomField> = customFields.filter(
      (_: IAreaScopeCustomField, i: number) => i !== index
    );
    form.setFieldValue(`detail.contentsMovedCustomFields`, updatedCustomFields);
  };

  const handleDeleteFloorDemoCustomField = (idx: number, index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.floorDemo?.[index]?.customFields;

    // If extraction custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields = customFields.filter((_: IAreaScopeCustomField, i: number) => i !== idx);
    form.setFieldValue(`detail.floorDemo[${index}].customFields`, updatedCustomFields);
  };

  const handleDeleteCabinetsCustomField = (idx: number, index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.cabinets?.[index]?.customFields;

    // If extraction custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields = customFields.filter((_: IAreaScopeCustomField, i: number) => i !== idx);
    form.setFieldValue(`detail.cabinets[${index}].customFields`, updatedCustomFields);
  };

  const handleDeleteAppliancesCustomField = (idx: number, index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.appliances?.[index]?.customFields;

    // If extraction custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields = customFields.filter((_: IAreaScopeCustomField, i: number) => i !== idx);
    form.setFieldValue(`detail.appliances[${index}].customFields`, updatedCustomFields);
  };

  const handleDeleteTrimCustomField = (idx: number, index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.trim?.[index]?.customFields;

    // If extraction custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields = customFields.filter((_: IAreaScopeCustomField, i: number) => i !== idx);
    form.setFieldValue(`detail.trim[${index}].customFields`, updatedCustomFields);
  };

  const handleDeleteInsulationCustomField = (idx: number, index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.insulation?.[index]?.customFields;

    // If extraction custom fields exist and the idx is valid, remove the item at idx
    const updatedCustomFields = customFields.filter((_: IAreaScopeCustomField, i: number) => i !== idx);
    form.setFieldValue(`detail.insulation[${index}].customFields`, updatedCustomFields);
  };

  const addNewCoverCustomField = () => {
    let coverCustomFields: Array<IAreaScopeCustomField> | undefined = form?.values?.detail?.coverCustomFields;
    if (coverCustomFields) {
      const newCoverCustomFields: Array<IAreaScopeCustomField> = [...form?.values.detail?.coverCustomFields];
      newCoverCustomFields.push({
        label: '',
        value: '',
      });
      form.setFieldValue('detail.coverCustomFields', newCoverCustomFields);
    } else {
      form.setFieldValue('detail.coverCustomFields', [{ label: '', value: '' }]);
    }
  };

  const addNewdoorsDetachedCustomField = () => {
    let coverCustomFields: Array<IAreaScopeCustomField> | undefined = form?.values?.detail?.doorsDetachedCustomFields;
    if (coverCustomFields) {
      const newCoverCustomFields: Array<IAreaScopeCustomField> = [...form?.values.detail?.doorsDetachedCustomFields];
      newCoverCustomFields.push({
        label: '',
        value: '',
      });
      form.setFieldValue('detail.doorsDetachedCustomFields', newCoverCustomFields);
    } else {
      form.setFieldValue('detail.doorsDetachedCustomFields', [{ label: '', value: '' }]);
    }
  };

  const addNewTrimFinishCustomField = () => {
    let coverCustomFields: Array<IAreaScopeCustomField> | undefined = form?.values?.detail?.trimCustomFields;
    if (coverCustomFields) {
      const newCoverCustomFields: Array<IAreaScopeCustomField> = [...form?.values.detail?.trimCustomFields];
      newCoverCustomFields.push({
        label: '',
        value: '',
      });
      form.setFieldValue('detail.trimCustomFields', newCoverCustomFields);
    } else {
      form.setFieldValue('detail.trimCustomFields', [{ label: '', value: '' }]);
    }
  };

  const addNewFloorDemoCustomField = (index: number) => {
    const newFloorDemoCustomFields = [...form.values.detail.floorDemo];
    newFloorDemoCustomFields[index].customFields.push({
      label: '',
      value: '',
    });
    form.setFieldValue('detail.floorDemo', newFloorDemoCustomFields);
  };

  const addNewCabinetsCustomField = (index: number) => {
    const newFloorDemoCustomFields = [...form.values.detail.cabinets];
    newFloorDemoCustomFields[index].customFields.push({
      label: '',
      value: '',
    });
    form.setFieldValue('detail.cabinets', newFloorDemoCustomFields);
  };

  const addNewInsulationCustomField = (index: number) => {
    const newFloorDemoCustomFields = [...form.values.detail.insulation];
    newFloorDemoCustomFields[index].customFields.push({
      label: '',
      value: '',
    });
    form.setFieldValue('detail.insulation', newFloorDemoCustomFields);
  };

  const addNewAppliancesCustomField = (index: number) => {
    const newFloorDemoCustomFields = [...form.values.detail.appliances];
    newFloorDemoCustomFields[index].customFields.push({
      label: '',
      value: '',
    });
    form.setFieldValue('detail.appliances', newFloorDemoCustomFields);
  };

  const addNewTrimCustomField = (index: number) => {
    const newFloorDemoCustomFields = [...form.values.detail.trim];
    newFloorDemoCustomFields[index].customFields.push({
      label: '',
      value: '',
    });
    form.setFieldValue('detail.trim', newFloorDemoCustomFields);
  };

  const handleDeleteCoverCustomField = (index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.coverCustomFields;

    const updatedCustomFields: Array<IAreaScopeCustomField> = customFields.filter(
      (_: IAreaScopeCustomField, i: number) => i !== index
    );
    form.setFieldValue(`detail.coverCustomFields`, updatedCustomFields);
  };

  const handleDeleteDoorsDetachedField = (index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.doorsDetachedCustomFields;

    const updatedCustomFields: Array<IAreaScopeCustomField> = customFields.filter(
      (_: IAreaScopeCustomField, i: number) => i !== index
    );
    form.setFieldValue(`detail.doorsDetachedCustomFields`, updatedCustomFields);
  };

  const handleDeleteTrimField = (index: number) => {
    const customFields: Array<IAreaScopeCustomField> = form.values?.detail?.trimCustomFields;

    const updatedCustomFields: Array<IAreaScopeCustomField> = customFields.filter(
      (_: IAreaScopeCustomField, i: number) => i !== index
    );
    form.setFieldValue(`detail.trimCustomFields`, updatedCustomFields);
  };
  const addAffectedField = (roomId: string, roomName: string) => {
    const newField = {
      roomId,
      roomName,
      readings: [
        {
          date: undefined,
          time: undefined,
          temp: undefined,
          rh: undefined,
          gpp: undefined,
        },
      ],
    };

    form.setFieldValue('detail.atmosphericReadings.affected', [
      ...(form.values.detail.atmosphericReadings?.affected ?? []),
      newField,
    ]);
  };
  const addDhField = (roomId: string, roomName: string) => {
    const newField = {
      roomId,
      roomName,
      readings: [
        {
          date: undefined,
          time: undefined,
          temp: undefined,
          rh: undefined,
          gpp: undefined,
        },
      ],
    };

    form.setFieldValue('detail.atmosphericReadings.dh', [
      ...(form.values.detail.atmosphericReadings?.dh ?? []),
      newField,
    ]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addNewUnaffectedItem = () => {
    form.setFieldValue('detail.atmosphericReadings.unaffected', [
      ...(form.values.detail.atmosphericReadings?.unaffected ?? []),
      {
        date: undefined,
        time: undefined,
        temp: undefined,
        rh: undefined,
        gpp: undefined,
      },
    ]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const addNewHvacItem = () => {
    form.setFieldValue('detail.atmosphericReadings.hvac', [
      ...(form.values.detail.atmosphericReadings?.hvac ?? []),
      {
        date: undefined,
        time: undefined,
        temp: undefined,
        rh: undefined,
        gpp: undefined,
      },
    ]);
  };
  const handleOpenAddRoom = (value: boolean) => {
    setOpenAddRoom(value);
  };
  const handleDelete = async () => {
    dispatch(areaScopeActions.changeOpenAlertDelete(true));
  };
  const handleOpenModal = (
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>,
    setFieldValueCallback: (roomId: string, roomName: string) => void
  ) => {
    setOpenModal(true);
    modalActionRef.current = setFieldValueCallback;
  };

  const handleSelectRoom = (roomId: string, roomName: string) => {
    if (modalActionRef.current) {
      modalActionRef.current(roomId, roomName);
    }
  };
  const onDeleteSelected = useCallback(async () => {
    if (areaId) {
      let payloadDeleteCustomers: IPayloadDelete = { ids: [areaId] };

      try {
        await areaScopeApi.delete(payloadDeleteCustomers);
        notify('success', t('deleteSuccessMessage'));
        dispatch(areaScopeActions.changeNeedRefreshData(true));
        dispatch(areaScopeActions.changeOpenAlertDelete(false));
      } catch (e: unknown) {
        catchError(e, (message) => {
          notify('error', message);
        });
      }
    }
    if (id) {
      navigate(`${adminRouteAbsolute.jobDetail.replace(':id', id)}#areaScope`);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, areaId, t]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(areaScopeActions.changeMenuSelectId(undefined));
    dispatch(areaScopeActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const addNewTrimItem = () => {
    form.setFieldValue('detail.trim', [
      ...(form.values.detail.trim ?? []),
      {
        base: undefined,
        cwn: undefined,
        tack: undefined,
        casings: undefined,
        customFields: [],
      },
    ]);
  };

  const addNewAffectedItem = (index: number) => {
    const newAffectedFields = [...form.values.detail.atmosphericReadings.affected];
    newAffectedFields[index].readings.push({
      date: '',
      time: '',
      temp: '',
      rh: '',
      gpp: '',
    });
    form.setFieldValue('detail.atmosphericReadings.affected', newAffectedFields);
  };

  const addNewDhItem = (index: number) => {
    const newDhFields = [...form.values.detail.atmosphericReadings.dh];
    newDhFields[index].readings.push({
      date: '',
      time: '',
      temp: '',
      rh: '',
      gpp: '',
    });
    form.setFieldValue('detail.atmosphericReadings.dh', newDhFields);
  };

  const addNewFloorDemoItem = () => {
    form.setFieldValue('detail.floorDemo', [
      ...(form.values.detail.floorDemo ?? []),
      {
        carpet: undefined,
        float: undefined,
        vinyl: undefined,
        ul: undefined,
        pad: undefined,
        hardwood: undefined,
        lam: undefined,
        gd: undefined,
        tile: undefined,
        customFields: [],
      },
    ]);
  };

  const addNewInsulationItem = () => {
    form.setFieldValue('detail.insulation', [
      ...(form.values.detail.insulation ?? []),
      {
        value: undefined,
        type: undefined,
        customFields: [],
      },
    ]);
  };

  const addNewAppliancesItem = () => {
    form.setFieldValue('detail.appliances', [
      ...(form.values.detail.appliances ?? []),
      {
        dw: undefined,
        rf: undefined,
        rge: undefined,
        rgg: undefined,
        mw: undefined,
        wm: undefined,
        dry: undefined,
        tlt: undefined,
        snk: undefined,
        psnk: undefined,
        caps: undefined,
        customFields: [],
      },
    ]);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateDrybookGoal = (form: any, index: number, dryStandardValue: string) => {
    const updatedDryStandard = parseFloat(dryStandardValue) || 0;

    let goal = '';

    if (updatedDryStandard) {
      const calculatedGoal = updatedDryStandard * 1.1;

      goal = Number.isInteger(calculatedGoal) ? calculatedGoal.toString() : calculatedGoal.toFixed(2);
    }

    // Use forEach to modify the drybook array
    form.values?.detail?.drybook?.forEach((drybook: any, idx: number) => {
      if (idx === index) {
        // Update the dryStandard and goal for the specific drybook
        drybook.dryStandard = updatedDryStandard;
        drybook.goal = goal;

        // Update the readings array with the new goal
        drybook.readings?.forEach((reading: any) => {
          reading.goal = goal;
        });
      }
    });

    // Return the updated form values after modifying the array
    return form.values?.detail?.drybook;
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateMoisturePoint = (form: any, index: number, moisturePointValue: string) => {
    const updatedMoisturePoint = parseFloat(moisturePointValue) || 0;

    // Use forEach to update the 'mp' field in each reading
    form.values?.detail?.drybook?.forEach((drybook: any, idx: number) => {
      if (idx === index) {
        drybook.moisturePoint = updatedMoisturePoint.toString();

        drybook.readings?.forEach((reading: any) => {
          // Set the 'mp' value to the moisturePoint value
          reading.mp = updatedMoisturePoint.toString();
        });
      }
    });

    // Return the updated form values after modifying the array
    return form.values?.detail?.drybook;
  };
  const addNewHeatSourceItem = () => {
    form.setFieldValue('detail.heatSource', [
      ...(form.values.detail.heatSource ?? []),
      {
        type: undefined,
        size: undefined,
        quantity: undefined,
        setDate: undefined,
        pickupDate: undefined,
        customFields: [],
      },
    ]);
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAreaChange = (value: string, drybookIndex: number) => {
    const selectedArea = areaScopes.find((area) => area.id === value);

    if (selectedArea) {
      const updatedReadings = form.values.detail.drybook[drybookIndex].readings.map((reading: any) => ({
        ...reading,
        area: selectedArea.name,
      }));

      form.setFieldValue(`detail.drybook[${drybookIndex}].readings`, updatedReadings);
      form.setFieldValue(`detail.drybook[${drybookIndex}].area`, selectedArea.id);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLocationChange = (value: string, drybookIndex: number) => {
    const selectedLocation = LOCATION.find((loc) => loc.id === value);

    if (selectedLocation) {
      const updatedReadings = form.values.detail.drybook[drybookIndex].readings.map((reading: any) => ({
        ...reading,
        location: selectedLocation.name,
      }));

      form.setFieldValue(`detail.drybook[${drybookIndex}].readings`, updatedReadings);
      form.setFieldValue(`detail.drybook[${drybookIndex}].location`, selectedLocation.id);
    }
  };

  const addNewCabinetsItem = () => {
    form.setFieldValue('detail.cabinets', [
      ...(form.values.detail.cabinets ?? []),
      {
        cabinets: undefined,
        counters: undefined,
        type: undefined,
        upper: undefined,
        lowers: undefined,
        fullHeight: undefined,
        holes: undefined,
        customFields: [],
      },
    ]);
  };

  const handleDeleteTrim = (index: number) => {
    const updatedFields = [...(form.values.detail.trim ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.trim', updatedFields);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteDryBookFields = (index: number) => {
    const updatedFields = [...(form.values.detail.drybook ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.drybook', updatedFields);
  };

  const handleDeleteExtraction = (index: number) => {
    const updatedFields = [...(form.values.detail.extraction ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.extraction', updatedFields);
  };

  const handleDeleteOutside = (index: number) => {
    const updatedFields = [...(form.values.detail.atmosphericReadings.outside ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.atmosphericReadings.outside', updatedFields);
  };

  const handleDeleteUnaffected = (index: number) => {
    const updatedFields = [...(form.values.detail.atmosphericReadings.unaffected ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.atmosphericReadings.unaffected', updatedFields);
  };

  const handleDeleteHvac = (index: number) => {
    const updatedFields = [...(form.values.detail.atmosphericReadings.hvac ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.atmosphericReadings.hvac', updatedFields);
  };

  const handleDeleteAffected = (idx: number, index: number) => {
    const affectedReadings: any = form.values?.detail?.atmosphericReadings?.affected?.[index]?.readings;

    // If affected readings exist and the idx is valid, remove the item at idx
    if (affectedReadings && idx >= 1 && idx < affectedReadings.length) {
      const updatedReadings = affectedReadings.filter((_: any, i: any) => i !== idx);

      form.setFieldValue(`detail.atmosphericReadings.affected[${index}].readings`, updatedReadings);
    }
  };

  const handleDeleteAffectedRoom = (index: number) => {
    const affectedRooms = form.values?.detail?.atmosphericReadings?.affected || [];

    if (affectedRooms && index >= 0 && index < affectedRooms.length) {
      const updatedAffectedRooms = affectedRooms.filter((_: any, i: number) => i !== index);

      form.setFieldValue('detail.atmosphericReadings.affected', updatedAffectedRooms);
    }
  };

  const handleDeleteDhRoom = (index: number) => {
    const dhRooms = form.values?.detail?.atmosphericReadings?.dh || [];

    if (dhRooms && index >= 0 && index < dhRooms.length) {
      const updatedDhRooms = dhRooms.filter((_: any, i: number) => i !== index);

      form.setFieldValue('detail.atmosphericReadings.dh', updatedDhRooms);
    }
  };
  const handleDeleteDh = (idx: number, index: number) => {
    const dhReadings: any = form.values?.detail?.atmosphericReadings?.dh?.[index]?.readings;

    // If dh readings exist and the idx is valid, remove the item at idx
    if (dhReadings && idx >= 1 && idx < dhReadings.length) {
      const updatedReadings = dhReadings.filter((_: any, i: any) => i !== idx);

      form.setFieldValue(`detail.atmosphericReadings.dh[${index}].readings`, updatedReadings);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDeleteDryBookItem = (idx: number, index: number) => {
    const drybookReadings: any = form.values?.detail?.drybook?.[index]?.readings;

    // If dh readings exist and the idx is valid, remove the item at idx
    if (drybookReadings && idx >= 1 && idx < drybookReadings.length) {
      const updatedReadings = drybookReadings.filter((_: any, i: any) => i !== idx);

      form.setFieldValue(`detail.drybook[${index}].readings`, updatedReadings);
    }
  };

  const handleDeleteHeatSource = (index: number) => {
    const updatedFields = [...(form.values.detail.heatSource ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.heatSource', updatedFields);
  };

  const handleDeleteAppliances = (index: number) => {
    const updatedFields = [...(form.values.detail.appliances ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.appliances', updatedFields);
  };

  const handleDeleteCabinets = (index: number) => {
    const updatedFields = [...(form.values.detail.cabinets ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.cabinets', updatedFields);
  };

  const handleDeleteFloorDemo = (index: number) => {
    const updatedFields = [...(form.values.detail.floorDemo ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.floorDemo', updatedFields);
  };

  const handleDeleteInsulation = (index: number) => {
    const updatedFields = [...(form.values.detail.insulation ?? [])];
    updatedFields.splice(index, 1);
    form.setFieldValue('detail.insulation', updatedFields);
  };
  const handleAddAffected = () => {
    handleOpenModal(setOpenAddRoom, addAffectedField);
    setModalType('affected');
  };

  const handleAddDh = () => {
    handleOpenModal(setOpenAddRoom, addDhField);
    setModalType('dh');
  };
  const renderExtractionField = (_item: any, index: number) => {
    return (
      <Card className='mb-[20px] p-7' key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full flex justify-end'>
            <TrashIcon
              className=' w-[20px] h-[20px] cursor-pointer mt-[8px]'
              onClick={() => handleDeleteExtraction(index)}
            />
          </Box>
        </BaseRow>
        <Box key={index}>
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid item xs={12} sm={6}>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>Hard Surface</InputLabel>
                  <TextField
                    name={`detail.extraction.${index}.hardSurface`}
                    placeholder='Hard Surface'
                    value={cast(form.values?.detail?.extraction[index].hardSurface)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, `detail.extraction.${index}.hardSurface`))}
                    helperText={getIn(form.errors, `detail.extraction.${index}.hardSurface`)}
                    fullWidth
                  />
                </Box>
                <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
              </BaseRow>
            </Grid>
            <Grid item xs={12} sm={6}>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>Carpet</InputLabel>
                  <TextField
                    name={`detail.extraction.${index}.carpet`}
                    placeholder='Carpet'
                    value={cast(form.values?.detail?.extraction[index].carpet)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, `detail.extraction.${index}.carpet`))}
                    helperText={getIn(form.errors, `detail.extraction.${index}.carpet`)}
                    fullWidth
                  />
                </Box>
                <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
              </BaseRow>
            </Grid>
          </Grid>
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid item xs={12} sm={4}>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>Bulk</InputLabel>
                  <TextField
                    name={`detail.extraction.${index}.bulk`}
                    placeholder='Bulk'
                    value={cast(form.values?.detail?.extraction[index].bulk)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, `detail.extraction.${index}.bulk`))}
                    helperText={getIn(form.errors, `detail.extraction.${index}.bulk`)}
                    fullWidth
                  />
                </Box>
                <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
              </BaseRow>
            </Grid>
            <Grid item xs={12} sm={4}>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>Heavy</InputLabel>
                  <TextField
                    name={`detail.extraction.${index}.heavy`}
                    placeholder='Heavy'
                    value={cast(form.values?.detail?.extraction[index].heavy)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, `detail.extraction.${index}.heavy`))}
                    helperText={getIn(form.errors, `detail.extraction.${index}.heavy`)}
                    fullWidth
                  />
                </Box>
                <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
              </BaseRow>
            </Grid>
            <Grid item xs={12} sm={4}>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full'>
                  <InputLabel>Weight</InputLabel>
                  <TextField
                    name={`detail.extraction.${index}.weight`}
                    placeholder='Weight'
                    value={cast(form.values?.detail?.extraction[index].weight)}
                    onChange={form.handleChange}
                    error={Boolean(getIn(form.errors, `detail.extraction.${index}.weight`))}
                    helperText={getIn(form.errors, `detail.extraction.${index}.weight`)}
                    fullWidth
                  />
                </Box>
                <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
              </BaseRow>
            </Grid>
            {(form.values?.detail?.extraction[index].customFields || []).map((customField: any, idx: any) => {
              const customFieldPath = `detail.extraction[${index}].customFields[${idx}]`;
              return (
                <Grid item xs={12} sm={4} key={`${index}-${idx}`}>
                  <Grid container spacing={3} className='mb-[24px]'>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.label`}
                        placeholder={t('properties.fieldName')}
                        value={
                          form.values?.detail?.extraction[index]?.customFields &&
                          form.values?.detail?.extraction[index]?.customFields[idx]?.label
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.value`}
                        placeholder={t('properties.fieldValue')}
                        value={
                          form.values?.detail?.extraction[index]?.customFields &&
                          form.values?.detail?.extraction[index]?.customFields[idx]?.value
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={2} className='flex items-center justify-center'>
                      <DeleteCircle
                        className='w-[20px] h-[20px] cursor-pointer'
                        onClick={() => handleDeleteExtractionCustomField(idx, index)}
                      />
                    </Grid> */}

                    <Box className='flex items-center justify-center ml-[20px]'>
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                        onClick={() => handleDeleteExtractionCustomField(idx, index)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Box>
        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              onClick={() => addNewExtractionCustomField(index)}
              className='whitespace-nowrap'
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Custom Field
            </Button>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderTrimField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Base</InputLabel>
            <TextField
              name={`detail.trim.${index}.base`}
              placeholder='Base'
              value={cast(form.values?.detail?.trim[index].base)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.trim.${index}.base`))}
              helperText={getIn(form.errors, `detail.trim.${index}.base`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
          <Box className='w-full'>
            <InputLabel>CWN</InputLabel>
            <TextField
              name={`detail.trim.${index}.cwn`}
              placeholder='CWN'
              value={cast(form.values?.detail?.trim[index].cwn)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.trim.${index}.cwn`))}
              helperText={getIn(form.errors, `detail.trim.${index}.cwn`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Tack</InputLabel>
            <TextField
              name={`detail.trim.${index}.tack`}
              placeholder='Tack'
              value={cast(form.values?.detail?.trim[index].tack)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.trim.${index}.tack`))}
              helperText={getIn(form.errors, `detail.trim.${index}.tack`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Casings</InputLabel>
            <TextField
              name={`detail.trim.${index}.casings`}
              placeholder='Casings'
              value={cast(form.values?.detail?.trim[index].casings)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.trim.${index}.casings`))}
              helperText={getIn(form.errors, `detail.trim.${index}.casings`)}
            />
          </Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Shoe</InputLabel>
            <TextField
              name={`detail.trim.${index}.shoe`}
              placeholder='Shoe'
              value={cast(form.values?.detail?.trim[index].shoe)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.trim.${index}.shoe`))}
              helperText={getIn(form.errors, `detail.trim.${index}.shoe`)}
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
              onClick={() => handleDeleteTrim(index)}
            />
          </Box>
        </BaseRow>
        <Grid item xs={12} sm={12}>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.trim[index].customFields || []).map((customField: any, idx: any) => {
              const customFieldPath = `detail.trim[${index}].customFields[${idx}]`;
              return (
                <Grid item xs={12} sm={4} key={`${index}-${idx}`}>
                  <Grid container spacing={3} className='mb-[24px]'>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.label`}
                        placeholder={t('properties.fieldName')}
                        value={
                          form.values?.detail?.trim[index]?.customFields &&
                          form.values?.detail?.trim[index]?.customFields[idx]?.label
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.value`}
                        placeholder={t('properties.fieldValue')}
                        value={
                          form.values?.detail?.trim[index]?.customFields &&
                          form.values?.detail?.trim[index]?.customFields[idx]?.value
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                      <DeleteCircle
                        className='w-[20px] h-[20px] cursor-pointer'
                        onClick={() => handleDeleteTrimCustomField(idx, index)}
                      />
                    </Grid> */}

                    <Box className='flex items-center justify-center ml-[20px]'>
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                        onClick={() => handleDeleteTrimCustomField(idx, index)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              className='whitespace-nowrap'
              onClick={() => addNewTrimCustomField(index)}
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Custom Field
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };
  const renderFloorDemoField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Carpet</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.carpet`}
              placeholder='Carpet'
              value={cast(form.values?.detail?.floorDemo[index].carpet)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.carpet`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.carpet`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>

          <Box className='w-full'>
            <InputLabel>Floating</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.float`}
              placeholder='Floating'
              value={cast(form.values?.detail?.floorDemo[index].float)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.float`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.float`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
        </BaseRow>

        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Pad</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.vinyl`}
              placeholder='Pad'
              value={cast(form.values?.detail?.floorDemo[index].vinyl)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.vinyl`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.vinyl`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
          <Box className='w-full'>
            <InputLabel>Engineered</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.ul`}
              placeholder='Engineered'
              value={cast(form.values?.detail?.floorDemo[index].ul)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.ul`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.ul`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Tile</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.pad`}
              placeholder='Tile'
              value={cast(form.values?.detail?.floorDemo[index].pad)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.pad`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.pad`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
          <Box className='w-full'>
            <InputLabel>Hardwood</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.hardwood`}
              placeholder='Hardwood'
              value={cast(form.values?.detail?.floorDemo[index].hardwood)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.hardwood`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.hardwood`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Sheet Flooring</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.lam`}
              placeholder='Sheet Flooring'
              value={cast(form.values?.detail?.floorDemo[index].lam)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.lam`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.lam`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Glued Down</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.gd`}
              placeholder='Glued Down'
              value={cast(form.values?.detail?.floorDemo[index].gd)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.gd`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.gd`)}
            />
          </Box>
          <Box className='w-full'>
            <InputLabel>Underlay</InputLabel>
            <TextField
              name={`detail.floorDemo.${index}.tile`}
              placeholder='Underlay'
              value={cast(form.values?.detail?.floorDemo[index].tile)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.floorDemo.${index}.tile`))}
              helperText={getIn(form.errors, `detail.floorDemo.${index}.tile`)}
            />
          </Box>
          <Box>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
              onClick={() => handleDeleteFloorDemo(index)}
            />
          </Box>
        </BaseRow>

        <Grid item xs={12} sm={12}>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.floorDemo[index].customFields || []).map((customField: any, idx: any) => {
              const customFieldPath = `detail.floorDemo[${index}].customFields[${idx}]`;
              return (
                <Grid item xs={12} sm={4} key={`${index}-${idx}`}>
                  <Grid container spacing={3} className='mb-[24px]'>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.label`}
                        placeholder={t('properties.fieldName')}
                        value={
                          form.values?.detail?.floorDemo[index]?.customFields &&
                          form.values?.detail?.floorDemo[index]?.customFields[idx]?.label
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.value`}
                        placeholder={t('properties.fieldValue')}
                        value={
                          form.values?.detail?.floorDemo[index]?.customFields &&
                          form.values?.detail?.floorDemo[index]?.customFields[idx]?.value
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                      <DeleteCircle
                        className='w-[20px] h-[20px] cursor-pointer'
                        onClick={() => handleDeleteFloorDemoCustomField(idx, index)}
                      />
                    </Grid> */}

                    <Box className='flex items-center justify-center ml-[20px]'>
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                        onClick={() => handleDeleteFloorDemoCustomField(idx, index)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              className='whitespace-nowrap'
              onClick={() => addNewFloorDemoCustomField(index)}
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Custom Field
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderInsulationField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Insulation</InputLabel>
            <TextField
              name={`detail.insulation.${index}.value`}
              placeholder='Insulation'
              value={cast(form.values?.detail?.insulation?.[index]?.value || '')}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.insulation.${index}.value`))}
              helperText={getIn(form.errors, `detail.insulation.${index}.value`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
              onClick={() => handleDeleteInsulation(index)}
            />
          </Box>
        </BaseRow>

        <BaseRow className='gap-[20px] mb-[20px]'>
          {['Batt', 'Blown', 'Spray'].map((type: string) => (
            <Box className='w-full flex items-center gap-[8px]' key={type}>
              <Checkbox
                checked={form.values?.detail?.insulation?.[index]?.types?.includes(type) || false}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const currentTypes = form.values?.detail?.insulation?.[index]?.types || [];
                  const updatedTypes = checked
                    ? [...currentTypes, type]
                    : currentTypes.filter((t: string) => t !== type);

                  form.setFieldValue(`detail.insulation.${index}.types`, updatedTypes);
                }}
              />
              <Box className='text-[14px] text-gray-500'>{type}</Box>
            </Box>
          ))}
        </BaseRow>

        <Grid item xs={12} sm={12}>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.insulation[index].customFields || []).map((customField: any, idx: any) => {
              const customFieldPath = `detail.insulation[${index}].customFields[${idx}]`;
              return (
                <Grid item xs={12} sm={4} key={`${index}-${idx}`}>
                  <Grid container spacing={3} className='mb-[24px]'>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.label`}
                        placeholder={t('properties.fieldName')}
                        value={
                          form.values?.detail?.insulation[index]?.customFields &&
                          form.values?.detail?.insulation[index]?.customFields[idx]?.label
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.value`}
                        placeholder={t('properties.fieldValue')}
                        value={
                          form.values?.detail?.insulation[index]?.customFields &&
                          form.values?.detail?.insulation[index]?.customFields[idx]?.value
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                      <DeleteCircle
                        className='w-[20px] h-[20px] cursor-pointer'
                        onClick={() => handleDeleteInsulationCustomField(idx, index)}
                      />
                    </Grid> */}
                    <Box className='flex items-center justify-center ml-[20px]'>
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                        onClick={() => handleDeleteInsulationCustomField(idx, index)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              className='whitespace-nowrap'
              onClick={() => addNewInsulationCustomField(index)}
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Custom Field
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderCabinetsField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Countertop Type</InputLabel>
            <TextField
              name={`detail.cabinets.${index}.type`}
              placeholder='Countertop Type'
              value={cast(form.values?.detail?.cabinets[index].type)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.cabinets.${index}.type`))}
              helperText={getIn(form.errors, `detail.cabinets.${index}.type`)}
            />
          </Box>
          <Box className='w-full'>
            <TextField
              className='mt-[20px]'
              name={`detail.cabinets.${index}.counters`}
              value={cast(form.values?.detail?.cabinets[index].counters)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.cabinets.${index}.counters`))}
              helperText={getIn(form.errors, `detail.cabinets.${index}.counters`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Uppers</InputLabel>
            <TextField
              name={`detail.cabinets.${index}.upper`}
              placeholder='Uppers'
              value={cast(form.values?.detail?.cabinets[index].upper)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.cabinets.${index}.upper`))}
              helperText={getIn(form.errors, `detail.cabinets.${index}.upper`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
          <Box className='w-full'>
            <InputLabel>Lowers</InputLabel>
            <TextField
              name={`detail.cabinets.${index}.lower`}
              placeholder='Lowers'
              value={cast(form.values?.detail?.cabinets[index].lower)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.cabinets.${index}.lower`))}
              helperText={getIn(form.errors, `detail.cabinets.${index}.lower`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Full Height</InputLabel>
            <TextField
              name={`detail.cabinets.${index}.fullHeight`}
              placeholder='Full Height'
              value={cast(form.values?.detail?.cabinets[index].fullHeight)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.cabinets.${index}.fullHeight`))}
              helperText={getIn(form.errors, `detail.cabinets.${index}.fullHeight`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
          <Box className='w-full'>
            <InputLabel>Backsplash</InputLabel>
            <TextField
              name={`detail.cabinets.${index}.backsplash`}
              placeholder='Backsplash'
              value={cast(form.values?.detail?.cabinets[index].backsplash)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.cabinets.${index}.backsplash`))}
              helperText={getIn(form.errors, `detail.cabinets.${index}.backsplash`)}
            />
          </Box>
          <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
          <Box className='w-full'>
            <InputLabel># Holes</InputLabel>
            <TextField
              name={`detail.cabinets.${index}.holes`}
              placeholder='# Holes'
              value={cast(form.values?.detail?.cabinets[index].holes)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.cabinets.${index}.holes`))}
              helperText={getIn(form.errors, `detail.cabinets.${index}.holes`)}
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
              onClick={() => handleDeleteCabinets(index)}
            />
          </Box>
        </BaseRow>
        <Grid item xs={12} sm={12}>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.cabinets[index].customFields || []).map((customField: any, idx: any) => {
              const customFieldPath = `detail.cabinets[${index}].customFields[${idx}]`;
              return (
                <Grid item xs={12} sm={4} key={`${index}-${idx}`}>
                  <Grid container spacing={3} className='mb-[24px]'>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.label`}
                        placeholder={t('properties.fieldName')}
                        value={
                          form.values?.detail?.cabinets[index]?.customFields &&
                          form.values?.detail?.cabinets[index]?.customFields[idx]?.label
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.value`}
                        placeholder={t('properties.fieldValue')}
                        value={
                          form.values?.detail?.cabinets[index]?.customFields &&
                          form.values?.detail?.cabinets[index]?.customFields[idx]?.value
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                      <DeleteCircle
                        className='w-[20px] h-[20px] cursor-pointer'
                        onClick={() => handleDeleteCabinetsCustomField(idx, index)}
                      />
                    </Grid> */}

                    <Box className='flex items-center justify-center ml-[20px]'>
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                        onClick={() => handleDeleteCabinetsCustomField(idx, index)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              className='whitespace-nowrap'
              onClick={() => addNewCabinetsCustomField(index)}
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Custom Field
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderHeatSourceField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Type</InputLabel>
            <Autocomplete
              freeSolo
              options={equipments?.map((type: any) => type.name) ?? []}
              value={cast(form.values?.detail?.heatSource[index].type)}
              onChange={(e, value) => {
                form.setFieldValue(`detail.heatSource[${index}].type`, cast(value));
              }}
              onInputChange={(e, value) => {
                form.setFieldValue(`detail.heatSource[${index}].type`, cast(value));
              }}
              renderInput={(params) => <TextField {...params} placeholder='Type' fullWidth />}
            />
          </Box>
          {/* <Box className='w-full'>
            <InputLabel>Size</InputLabel>
            <TextField
              name={`detail.heatSource.${index}.size`}
              placeholder='Size'
              value={cast(form.values?.detail?.heatSource[index].size)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.heatSource.${index}.size`))}
              helperText={getIn(form.errors, `detail.heatSource.${index}.size`)}
            />
          </Box> */}
          <Box className='w-full'>
            <InputLabel>Quantity</InputLabel>
            <TextField
              name={`detail.heatSource.${index}.quantity`}
              placeholder='Quantity'
              value={cast(form.values?.detail?.heatSource[index].quantity)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.heatSource.${index}.quantity`))}
              helperText={getIn(form.errors, `detail.heatSource.${index}.quantity`)}
            />
          </Box>
          <Box className='w-full'>
            <InputLabel>Set Date</InputLabel>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.detail?.heatSource[index].setDate}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`detail.heatSource.${index}.setDate`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `detail.heatSource.${index}.setDate`))}
              helperText={getIn(form.errors, `detail.heatSource.${index}.setDate`)}
            />
          </Box>
          <Box className='w-full'>
            <InputLabel>Pickup Date</InputLabel>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.detail?.heatSource[index].pickupDate}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`detail.heatSource.${index}.pickupDate`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `detail.heatSource.${index}.pickupDate`))}
              helperText={getIn(form.errors, `detail.heatSource.${index}.pickupDate`)}
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
              onClick={() => handleDeleteHeatSource(index)}
            />
          </Box>
        </BaseRow>
      </Box>
    );
  };
  const renderAppliancesField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Dishwasher</InputLabel>
            <TextField
              name={`detail.appliances.${index}.dw`}
              placeholder='Dishwasher'
              value={cast(form.values?.detail?.appliances[index].dw)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.dw`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.dw`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Refrigerator</InputLabel>
            <TextField
              name={`detail.appliances.${index}.rf`}
              placeholder='Refrigerator'
              value={cast(form.values?.detail?.appliances[index].rf)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.rf`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.rf`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Dryer</InputLabel>
            <TextField
              name={`detail.appliances.${index}.rge`}
              placeholder='Dryer'
              value={cast(form.values?.detail?.appliances[index].rge)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.rge`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.rge`)}
            />
          </Box>
        </BaseRow>

        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Range - Gas</InputLabel>
            <TextField
              name={`detail.appliances.${index}.rgg`}
              placeholder='Range - Gas'
              value={cast(form.values?.detail?.appliances[index].rgg)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.rgg`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.rgg`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Microwave</InputLabel>{' '}
            <TextField
              name={`detail.appliances.${index}.mw`}
              placeholder='Microwave'
              value={cast(form.values?.detail?.appliances[index].mw)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.mw`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.mw`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Washer</InputLabel>
            <TextField
              name={`detail.appliances.${index}.wm`}
              placeholder='Washer'
              value={cast(form.values?.detail?.appliances[index].wm)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.wm`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.wm`)}
            />
          </Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Range - Electric</InputLabel>
            <TextField
              name={`detail.appliances.${index}.dry`}
              placeholder='Range - Electric'
              value={cast(form.values?.detail?.appliances[index].dry)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.dry`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.dry`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Toilet</InputLabel>
            <TextField
              name={`detail.appliances.${index}.tlt`}
              placeholder='Toilet'
              value={cast(form.values?.detail?.appliances[index].tlt)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.tlt`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.tlt`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Sink</InputLabel>
            <TextField
              name={`detail.appliances.${index}.snk`}
              placeholder='Sink'
              value={cast(form.values?.detail?.appliances[index].snk)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.snk`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.snk`)}
            />
          </Box>
        </BaseRow>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <InputLabel>Pedestal Sink</InputLabel>
            <TextField
              name={`detail.appliances.${index}.psnk`}
              placeholder='Pedestal Sink'
              value={cast(form.values?.detail?.appliances[index].psnk)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.psnk`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.psnk`)}
            />
          </Box>

          <Box className='w-full'>
            <InputLabel>Caps</InputLabel>
            <TextField
              name={`detail.appliances.${index}.caps`}
              placeholder='Caps'
              value={cast(form.values?.detail?.appliances[index].caps)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.appliances.${index}.caps`))}
              helperText={getIn(form.errors, `detail.appliances.${index}.caps`)}
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
              onClick={() => handleDeleteAppliances(index)}
            />
          </Box>
        </BaseRow>

        <Grid item xs={12} sm={12}>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.appliances[index].customFields || []).map((customField: any, idx: any) => {
              const customFieldPath = `detail.appliances[${index}].customFields[${idx}]`;
              return (
                <Grid item xs={12} sm={4} key={`${index}-${idx}`}>
                  <Grid container spacing={3} className='mb-[24px]'>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.label`}
                        placeholder={t('properties.fieldName')}
                        value={
                          form.values?.detail?.appliances[index]?.customFields &&
                          form.values?.detail?.appliances[index]?.customFields[idx]?.label
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={5} sm={5}>
                      <TextField
                        name={`${customFieldPath}.value`}
                        placeholder={t('properties.fieldValue')}
                        value={
                          form.values?.detail?.appliances[index]?.customFields &&
                          form.values?.detail?.appliances[index]?.customFields[idx]?.value
                        }
                        onChange={form.handleChange}
                        fullWidth
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                      <DeleteCircle
                        className='w-[20px] h-[20px] cursor-pointer'
                        onClick={() => handleDeleteAppliancesCustomField(idx, index)}
                      />
                    </Grid> */}

                    <Box className='flex items-center justify-center ml-[20px]'>
                      <DeleteCircle
                        className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                        onClick={() => handleDeleteAppliancesCustomField(idx, index)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid container spacing={3} className='mb-[24px]'>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          <Grid className='flex justify-end' item xs={12} sm={4}>
            <Button
              className='whitespace-nowrap'
              onClick={() => addNewAppliancesCustomField(index)}
              startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            >
              Add Custom Field
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderOutsideField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.detail?.atmosphericReadings?.outside[index].date}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`detail.atmosphericReadings.outside.${index}.date`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.outside.${index}.date`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.outside.${index}.date`)}
            />
          </Box>
          <Box className='w-full'>
            <BaseTimePicker
              placeholder='Select Time'
              value={
                form.values.detail?.atmosphericReadings?.outside[index].time
                  ? dayjs(form.values.detail?.atmosphericReadings?.outside[index].time, 'HH:mm').toDate()
                  : undefined
              }
              onChange={(time) => {
                const startTime = dayjs(time).format('HH:mm');
                form.setFieldValue(`detail.atmosphericReadings.outside.${index}.time`, startTime);
              }}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.outside.${index}.time`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.outside.${index}.time`)}
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.outside.${index}.temp`}
              placeholder='Temp'
              value={cast(form.values.detail?.atmosphericReadings?.outside[index].temp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.outside.${index}.temp`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.outside.${index}.temp`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.outside.${index}.rh`}
              placeholder='RH'
              value={cast(form.values.detail?.atmosphericReadings?.outside[index].rh)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.outside.${index}.rh`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.outside.${index}.rh`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.outside.${index}.gpp`}
              placeholder='GPP'
              value={cast(form.values.detail?.atmosphericReadings?.outside[index].gpp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.outside.${index}.gpp`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.outside.${index}.gpp`)}
              fullWidth
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[8px]'
              onClick={() => handleDeleteOutside(index)}
            />
          </Box>
        </BaseRow>
      </Box>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderUnaffectedField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.detail?.atmosphericReadings?.unaffected[index].date}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`detail.atmosphericReadings.unaffected.${index}.date`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.date`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.date`)}
            />
          </Box>
          <Box className='w-full'>
            <BaseTimePicker
              placeholder='Select Time'
              value={
                form.values.detail?.atmosphericReadings?.unaffected[index].time
                  ? dayjs(form.values.detail?.atmosphericReadings?.unaffected[index].time, 'HH:mm').toDate()
                  : undefined
              }
              onChange={(time) => {
                const startTime = dayjs(time).format('HH:mm');
                form.setFieldValue(`detail.atmosphericReadings.unaffected.${index}.time`, startTime);
              }}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.time`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.time`)}
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.unaffected.${index}.temp`}
              placeholder='Temp'
              value={cast(form.values.detail?.atmosphericReadings?.unaffected[index].temp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.temp`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.temp`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.unaffected.${index}.rh`}
              placeholder='RH'
              value={cast(form.values.detail?.atmosphericReadings?.unaffected[index].rh)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.rh`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.rh`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.unaffected.${index}.gpp`}
              placeholder='GPP'
              value={cast(form.values.detail?.atmosphericReadings?.unaffected[index].gpp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.gpp`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.unaffected.${index}.gpp`)}
              fullWidth
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[8px]'
              onClick={() => handleDeleteUnaffected(index)}
            />
          </Box>
        </BaseRow>
      </Box>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderHvacField = (_item: any, index: number) => {
    return (
      <Box key={index}>
        <BaseRow className='gap-[20px] mb-[20px]'>
          <Box className='w-full'>
            <BaseDatePicker
              placeholder='Select Date'
              value={form.values.detail?.atmosphericReadings?.hvac[index].date}
              onChange={(date) => {
                if (date) {
                  const selectedDate = dayjs(date).format('MMM DD, YYYY');

                  form.setFieldValue(`detail.atmosphericReadings.hvac.${index}.date`, selectedDate);
                }
              }}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.date`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.date`)}
            />
          </Box>
          <Box className='w-full'>
            <BaseTimePicker
              placeholder='Select Time'
              value={
                form.values.detail?.atmosphericReadings?.hvac[index].time
                  ? dayjs(form.values.detail?.atmosphericReadings?.hvac[index].time, 'HH:mm').toDate()
                  : undefined
              }
              onChange={(time) => {
                const startTime = dayjs(time).format('HH:mm');
                form.setFieldValue(`detail.atmosphericReadings.hvac.${index}.time`, startTime);
              }}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.time`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.time`)}
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.hvac.${index}.temp`}
              placeholder='Temp'
              value={cast(form.values.detail?.atmosphericReadings?.hvac[index].temp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.temp`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.temp`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.hvac.${index}.rh`}
              placeholder='RH'
              value={cast(form.values.detail?.atmosphericReadings?.hvac[index].rh)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.rh`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.rh`)}
              fullWidth
            />
          </Box>
          <Box className='w-full'>
            <TextField
              name={`detail.atmosphericReadings.hvac.${index}.gpp`}
              placeholder='GPP'
              value={cast(form.values.detail?.atmosphericReadings?.hvac[index].gpp)}
              onChange={form.handleChange}
              error={Boolean(getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.gpp`))}
              helperText={getIn(form.errors, `detail.atmosphericReadings.hvac.${index}.gpp`)}
              fullWidth
            />
          </Box>
          <Box className='w-[5px]'>
            <DeleteCircle
              className='w-[16px] h-[16px] cursor-pointer mt-[8px]'
              onClick={() => handleDeleteHvac(index)}
            />
          </Box>
        </BaseRow>
      </Box>
    );
  };

  useEffect(() => {
    setRoomArea(form.values.name);

    if (areaId === 'create' && form.values.detail.atmosphericReadings) {
      const atmosphericReadings = form.values.detail.atmosphericReadings;
      const { affected, dh } = atmosphericReadings;

      const updateRoomName = (items: any[]) =>
        items.map((item) => ({
          ...item,
          roomName: !item.roomId ? form.values.name : item.roomName,
        }));

      if (affected) {
        form.values.detail.atmosphericReadings.affected = updateRoomName(affected);
      }

      if (dh) {
        form.values.detail.atmosphericReadings.dh = updateRoomName(dh);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId, form.values.name]);

  //#endregion Handle Function
  return (
    <Box>
      <DialogTitle>
        <IconButton onClick={handleSetOpen} className='p-0 w-[40px] h-[40px]'>
          <ArrowLeftDoubleIcon className='w-[11px] mb-[5px]' />
        </IconButton>
        {areaId === 'create' ? t('addNew') : t('editAreaScope')}
      </DialogTitle>

      <DialogContent>
        <Box component='form'>
          {/* Room Area */}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid item xs={12} sm={12}>
              <InputLabel required={true}>Room Area</InputLabel>
              <TextField
                name='name'
                placeholder='Room Area'
                value={cast(form.values?.name)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'name'))}
                helperText={getIn(form.errors, 'name')}
              />
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Length</InputLabel>
              <TextField
                name='detail.length'
                placeholder='L'
                value={cast(form.values?.detail.length)}
                onChange={(e) => {
                  let value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    value = value.replace(/^0+(?=\d)/, '');
                    form.setFieldValue('detail.length', value);
                  }
                }}
                error={Boolean(getIn(form.errors, 'detail.length'))}
                helperText={getIn(form.errors, 'detail.length')}
              />
            </Box>

            <Box className='w-[20px]'>
              <MultipleIcon className='mt-[20px]' />
            </Box>
            <Box className='w-full'>
              <InputLabel>Width</InputLabel>
              <TextField
                name='detail.width'
                placeholder='W'
                value={cast(form.values?.detail.width)}
                onChange={(e) => {
                  let value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    value = value.replace(/^0+(?=\d)/, '');
                    form.setFieldValue('detail.width', value);
                  }
                }}
                error={Boolean(getIn(form.errors, 'detail.width'))}
                helperText={getIn(form.errors, 'detail.width')}
              />
            </Box>
            <Box className='w-[20px]'>
              <MultipleIcon className='mt-[20px]' />
            </Box>
            <Box className='w-full'>
              <InputLabel>Height</InputLabel>
              <TextField
                name='detail.height'
                placeholder='H'
                value={cast(form.values?.detail.height)}
                onChange={(e) => {
                  let value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    value = value.replace(/^0+(?=\d)/, '');
                    form.setFieldValue('detail.height', value);
                  }
                }}
                error={Boolean(getIn(form.errors, 'detail.height'))}
                helperText={getIn(form.errors, 'detail.height')}
              />
            </Box>
            <Box className='w-[20px]'>
              <EqualIcon className='mt-[35px]' />
            </Box>
            <Box className='w-full'>
              <InputLabel>Cubic Feet</InputLabel>
              <TextField
                name='detail.total'
                placeholder='ft'
                value={cast(form.values?.detail.total)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.total'))}
                helperText={getIn(form.errors, 'detail.total')}
                disabled
              />
            </Box>
          </BaseRow>
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Box className='w-full'>
                <InputLabel>Square Feet</InputLabel>
                <TextField
                  name='detail.multiplication'
                  placeholder='L X W'
                  value={cast(form.values?.detail.multiplication)}
                  onChange={form.handleChange}
                  error={Boolean(getIn(form.errors, 'detail.multiplication'))}
                  helperText={getIn(form.errors, 'detail.multiplication')}
                  disabled
                />
              </Box>
            </Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle>Extraction</DialogTitle>
          </Grid>
          {form.values.detail.extraction?.map((item: any, index: number) => renderExtractionField(item, index))}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                onClick={addNewExtractionItem}
                className='whitespace-nowrap'
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid item xs={12} sm={12}>
              <InputLabel>Contents Moved</InputLabel>
              <NumericFormat
                customInput={TextField}
                name='detail.contentsMoved'
                placeholder='Contents Moved'
                value={cast(form.values?.detail.contentsMoved)}
                onValueChange={(val) => form.setFieldValue('detail.contentsMoved', val.floatValue)}
                thousandSeparator={true}
                error={Boolean(getIn(form.errors, 'detail.contentsMoved'))}
                helperText={getIn(form.errors, 'detail.contentsMoved')}
                InputProps={{
                  endAdornment: <InputAdornment position='end'>hours</InputAdornment>,
                }}
                decimalScale={2}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Grid container spacing={3} className='mb-[24px]'>
                {(form.values?.detail?.contentsMovedCustomFields || []).map(
                  (customField: { label: string; value: string }, index: number) => {
                    const customFieldPath = `detail.contentsMovedCustomFields[${index}]`;
                    return (
                      <Grid item xs={12} sm={4} key={`contents-moved-${index}`}>
                        <Grid container spacing={3} className='mb-[24px]'>
                          <Grid item xs={5} sm={5}>
                            <TextField
                              name={`${customFieldPath}.label`}
                              placeholder={t('properties.fieldName')}
                              value={
                                form.values?.detail?.contentsMovedCustomFields &&
                                form.values?.detail?.contentsMovedCustomFields[index]?.label
                              }
                              onChange={form.handleChange}
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={5} sm={5}>
                            <TextField
                              name={`${customFieldPath}.value`}
                              placeholder={t('properties.fieldValue')}
                              value={
                                form.values?.detail?.contentsMovedCustomFields &&
                                form.values?.detail?.contentsMovedCustomFields[index]?.value
                              }
                              onChange={form.handleChange}
                              fullWidth
                            />
                          </Grid>
                          {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                            <DeleteCircle
                              className='w-[20px] h-[20px] cursor-pointer'
                              onClick={() => handleDeleteCotentMovedCustomField(index)}
                            />
                          </Grid> */}

                          <Box className='flex items-center justify-center ml-[20px]'>
                            <DeleteCircle
                              className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                              onClick={() => handleDeleteCotentMovedCustomField(index)}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    );
                  }
                )}
              </Grid>
            </Grid>
            <Grid container spacing={3} className='mb-[24px]'>
              <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
              <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
              <Grid className='flex justify-end' item xs={12} sm={4}>
                <Button
                  className='whitespace-nowrap'
                  onClick={addNewContentMovedCustomField}
                  startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
                >
                  Add Custom Field
                </Button>
              </Grid>
            </Grid>
          </Grid>
          {/* Cover/Protect */}
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle>Cover/Protect</DialogTitle>
          </Grid>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Containment</InputLabel>
              <TextField
                name='detail.containment'
                placeholder='Containment'
                value={cast(form.values?.detail?.containment)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.containment'))}
                helperText={getIn(form.errors, 'detail.containment')}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
            <Box className='w-full'>
              <InputLabel>Zippers</InputLabel>
              <TextField
                name='detail.zippers'
                placeholder='Zippers'
                value={cast(form.values?.detail?.zippers)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.zippers'))}
                helperText={getIn(form.errors, 'detail.zippers')}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Poles</InputLabel>
              <TextField
                name='detail.poles'
                placeholder='Poles'
                value={cast(form.values?.detail?.poles)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.poles'))}
                helperText={getIn(form.errors, 'detail.poles')}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel>Lay Flat</InputLabel>
              <TextField
                name='detail.layFlat'
                placeholder='Lay Flat'
                value={cast(form.values?.detail?.layFlat)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.layFlat'))}
                helperText={getIn(form.errors, 'detail.layFlat')}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
            <Box className='w-full'>
              <InputLabel>Floor Protection</InputLabel>
              <TextField
                name='detail.floorProtection'
                placeholder='Floor Protection'
                value={cast(form.values?.detail?.floorProtection)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.floorProtection'))}
                helperText={getIn(form.errors, 'detail.floorProtection')}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Mask</InputLabel>
              <TextField
                name='detail.mask'
                placeholder='Mask'
                value={cast(form.values?.detail?.mask)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.mask'))}
                helperText={getIn(form.errors, 'detail.mask')}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
            <Box className='w-full'>
              <InputLabel>Foam Blocks</InputLabel>
              <TextField
                name='detail.foamBlocks'
                placeholder='Foam Blocks'
                value={cast(form.values?.detail?.foamBlocks)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.foamBlocks'))}
                helperText={getIn(form.errors, 'detail.foamBlocks')}
              />
            </Box>
            <Box className='w-full'>
              <InputLabel>Box / Bubble</InputLabel>
              <TextField
                name='detail.boxBubble'
                placeholder='Box / Bubble'
                value={cast(form.values?.detail?.boxBubble)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.boxBubble'))}
                helperText={getIn(form.errors, 'detail.boxBubble')}
              />
            </Box>
          </BaseRow>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.coverCustomFields || []).map(
              (customField: { label: string; value: string }, index: number) => {
                const customFieldPath = `detail.coverCustomFields[${index}]`;
                return (
                  <Grid item xs={12} sm={4} key={`cover-${index}`}>
                    <Grid container spacing={3} className='mb-[24px]'>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.label`}
                          placeholder={t('properties.fieldName')}
                          value={
                            form.values?.detail?.coverCustomFields &&
                            form.values?.detail?.coverCustomFields[index]?.label
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.value`}
                          placeholder={t('properties.fieldValue')}
                          value={
                            form.values?.detail?.coverCustomFields &&
                            form.values?.detail?.coverCustomFields[index]?.value
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                        <DeleteCircle
                          className='w-[20px] h-[20px] cursor-pointer'
                          onClick={() => handleDeleteCoverCustomField(index)}
                        />
                      </Grid> */}
                      <Box className='flex items-center justify-center ml-[20px]'>
                        <DeleteCircle
                          className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                          onClick={() => handleDeleteCoverCustomField(index)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                );
              }
            )}
          </Grid>
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                className='whitespace-nowrap'
                onClick={addNewCoverCustomField}
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Custom Field
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          {/*  Trim / Finish (lf) */}
          <Grid container spacing={3}>
            <DialogTitle> {t('trimFinish')}</DialogTitle>
          </Grid>
          {form.values.detail.trim?.map((item: any, index: number) => renderTrimField(item, index))}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                onClick={addNewTrimItem}
                className='whitespace-nowrap'
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Drywall</InputLabel>
              <TextField
                name='detail.drywall.drywall'
                placeholder='Drywall'
                value={cast(form.values?.detail?.drywall?.drywall)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.drywall.drywall'))}
                helperText={getIn(form.errors, 'detail.drywall.drywall')}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
            <Box className='w-full'>
              <InputLabel># Holes</InputLabel>
              <TextField
                name='detail.drywall.holes'
                placeholder='# Holes'
                value={cast(form.values?.detail?.drywall?.holes)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.drywall.holes'))}
                helperText={getIn(form.errors, 'detail.drywall.holes')}
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Flood Cut</InputLabel>
              <BaseSelect
                name={`detail.floodCuts.dispose`}
                value={cast(form.values?.detail?.floodCuts?.dispose)}
                options={FLOOR_CUTS}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.floodCuts.dispose`))}
                helperText={getIn(form.errors, `detail.floodCuts.dispose`)}
              ></BaseSelect>
            </Box>
            <Box className='w-full'>
              <InputLabel>lf</InputLabel>
              <TextField
                name='detail.drywall.total'
                placeholder='lf'
                value={cast(form.values?.detail.drywall?.total)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.drywall.total'))}
                helperText={getIn(form.errors, 'detail.drywall.total')}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>lf</Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Other Wall Type</InputLabel>
              <TextField
                name='detail.drywall.otherWallType'
                placeholder='Other Wall Type'
                value={cast(form.values?.detail.drywall?.otherWallType)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.drywall.otherWallType'))}
                helperText={getIn(form.errors, 'detail.drywall.otherWallType')}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel>sf</InputLabel>
              <TextField
                name='detail.drywall.sf'
                placeholder='sf'
                value={cast(form.values?.detail.drywall?.sf)}
                onChange={(e) => {
                  let value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    value = value.replace(/^0+(?=\d)/, '');
                    form.setFieldValue('detail.drywall.sf', value);
                  }
                }}
                error={Boolean(getIn(form.errors, 'detail.drywall.sf'))}
                helperText={getIn(form.errors, 'detail.drywall.sf')}
              />
            </Box>
          </BaseRow>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.trimCustomFields || []).map(
              (customField: { label: string; value: string }, index: number) => {
                const customFieldPath = `detail.trimCustomFields[${index}]`;
                return (
                  <Grid item xs={12} sm={4} key={`cover-${index}`}>
                    <Grid container spacing={3} className='mb-[24px]'>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.label`}
                          placeholder={t('properties.fieldName')}
                          value={
                            form.values?.detail?.trimCustomFields && form.values?.detail?.trimCustomFields[index]?.label
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.value`}
                          placeholder={t('properties.fieldValue')}
                          value={
                            form.values?.detail?.trimCustomFields && form.values?.detail?.trimCustomFields[index]?.value
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                        <DeleteCircle
                          className='w-[20px] h-[20px] cursor-pointer'
                          onClick={() => handleDeleteTrimField(index)}
                        />
                      </Grid> */}

                      <Box className='flex items-center justify-center ml-[20px]'>
                        <DeleteCircle
                          className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                          onClick={() => handleDeleteTrimField(index)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                );
              }
            )}
          </Grid>
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                className='whitespace-nowrap'
                onClick={addNewTrimFinishCustomField}
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Custom Field
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle> {t('insulation')}</DialogTitle>
          </Grid>
          {form.values.detail.insulation?.map((item: any, index: number) => renderInsulationField(item, index))}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                onClick={addNewInsulationItem}
                className='whitespace-nowrap'
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle className='!text-[15px]'>Floor Demo</DialogTitle>
          </Grid>
          {form.values.detail.floorDemo?.map((item: any, index: number) => renderFloorDemoField(item, index))}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                onClick={addNewFloorDemoItem}
                className='whitespace-nowrap'
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle className='!text-[15px]'>Countertops & Cabinetry</DialogTitle>
          </Grid>
          {form.values.detail.cabinets?.map((item: any, index: number) => renderCabinetsField(item, index))}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                onClick={addNewCabinetsItem}
                className='whitespace-nowrap'
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle className='!text-[15px]'>Doors Detached</DialogTitle>
          </Grid>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Slabs</InputLabel>
              <TextField
                name={`detail.doorsDetached.slabs`}
                placeholder='Slabs'
                value={cast(form.values?.detail?.doorsDetached?.slabs)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.doorsDetached.slabs`))}
                helperText={getIn(form.errors, `detail.doorsDetached.slabs`)}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel>Dispose</InputLabel>
              <BaseSelect
                name={`detail.doorsDetached.dispose`}
                value={cast(form.values?.detail?.doorsDetached?.dispose)}
                options={DISPOSE}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.doorsDetached.dispose`))}
                helperText={getIn(form.errors, `detail.doorsDetached.dispose`)}
              ></BaseSelect>
            </Box>
          </BaseRow>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.doorsDetachedCustomFields || []).map(
              (customField: { label: string; value: string }, index: number) => {
                const customFieldPath = `detail.doorsDetachedCustomFields[${index}]`;
                return (
                  <Grid item xs={12} sm={4} key={`cover-${index}`}>
                    <Grid container spacing={3} className='mb-[24px]'>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.label`}
                          placeholder={t('properties.fieldName')}
                          value={
                            form.values?.detail?.doorsDetachedCustomFields &&
                            form.values?.detail?.doorsDetachedCustomFields[index]?.label
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.value`}
                          placeholder={t('properties.fieldValue')}
                          value={
                            form.values?.detail?.doorsDetachedCustomFields &&
                            form.values?.detail?.doorsDetachedCustomFields[index]?.value
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                        <DeleteCircle
                          className='w-[20px] h-[20px] cursor-pointer'
                          onClick={() => handleDeleteDoorsDetachedField(index)}
                        />
                      </Grid> */}

                      <Box className='flex items-center justify-center ml-[20px]'>
                        <DeleteCircle
                          className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                          onClick={() => handleDeleteDoorsDetachedField(index)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                );
              }
            )}
          </Grid>
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                className='whitespace-nowrap'
                onClick={addNewdoorsDetachedCustomField}
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Custom Field
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle className='!text-[15px]'>Appliances/Fixtures</DialogTitle>
          </Grid>
          {form.values.detail.appliances?.map((item: any, index: number) => renderAppliancesField(item, index))}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                onClick={addNewAppliancesItem}
                className='whitespace-nowrap'
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <Box className='w-full flex items-center gap-2'>
              <DialogTitle className='!text-[15px]'>Clean</DialogTitle>
              <Grid item sm={4}>
                <BaseSelect
                  name={`detail.clean`}
                  value={cast(form.values?.detail?.clean)}
                  placeholder='Clean'
                  options={CLEAN}
                  onChange={form.handleChange}
                ></BaseSelect>
              </Grid>
            </Box>
          </Grid>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Anti Microbial/Biocide</InputLabel>
              <TextField
                name={'detail.antiMicrobial'}
                placeholder='Anti Microbial/Biocide'
                value={cast(form.values?.detail?.antiMicrobial)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, 'detail.antiMicrobial'))}
                helperText={getIn(form.errors, 'detail.antiMicrobial')}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
            <Box className='w-full'>
              <InputLabel>Wet Wipe</InputLabel>
              <TextField
                name={`detail.wetWipe`}
                placeholder='Wet Wipe'
                value={cast(form.values?.detail?.wetWipe)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.wetWipe`))}
                helperText={getIn(form.errors, `detail.wetWipe`)}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
            <Box className='w-full'>
              <InputLabel>HEPA Vac</InputLabel>
              <TextField
                name={`detail.hepaVac`}
                placeholder='HEPA Vac'
                value={cast(form.values?.detail?.hepaVac)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.hepaVac`))}
                helperText={getIn(form.errors, `detail.hepaVac`)}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Stain Reducer</InputLabel>
              <TextField
                name={`detail.stainReducer`}
                placeholder='Stain Reducer'
                value={cast(form.values?.detail?.stainReducer)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.stainReducer`))}
                helperText={getIn(form.errors, `detail.stainReducer`)}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
            <Box className='w-full'>
              <InputLabel>Scrub/Sand</InputLabel>
              <TextField
                name={`detail.scrub`}
                placeholder='Scrub/Sand'
                value={cast(form.values?.detail?.scrub)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.scrub`))}
                helperText={getIn(form.errors, `detail.scrub`)}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
            <Box className='w-full'>
              <InputLabel>Sealant</InputLabel>
              <TextField
                name={`detail.sealant`}
                placeholder='Sealant'
                value={cast(form.values?.detail?.sealant)}
                onChange={form.handleChange}
                error={Boolean(getIn(form.errors, `detail.sealant`))}
                helperText={getIn(form.errors, `detail.sealant`)}
              />
            </Box>
            <Box className='w-[20px] mt-[30px] mr-[20px] color-disabled'>sf</Box>
          </BaseRow>
          <Grid container spacing={3} className='mb-[24px]'>
            {(form.values?.detail?.cleanCustomFields || []).map(
              (customField: { label: string; value: string }, index: number) => {
                const customFieldPath = `detail.cleanCustomFields[${index}]`;
                return (
                  <Grid item xs={12} sm={4} key={`clean-${index}`}>
                    <Grid container spacing={3} className='mb-[24px]'>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.label`}
                          placeholder={t('properties.fieldName')}
                          value={
                            form.values?.detail?.cleanCustomFields &&
                            form.values?.detail?.cleanCustomFields[index]?.label
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={5} sm={5}>
                        <TextField
                          name={`${customFieldPath}.value`}
                          placeholder={t('properties.fieldValue')}
                          value={
                            form.values?.detail?.cleanCustomFields &&
                            form.values?.detail?.cleanCustomFields[index]?.value
                          }
                          onChange={form.handleChange}
                          fullWidth
                        />
                      </Grid>
                      {/* <Grid item xs={12} sm={1} className='flex items-center justify-center'>
                        <DeleteCircle
                          className='w-[20px] h-[20px] cursor-pointer'
                          onClick={() => handleDeleteCleanCustomField(index)}
                        />
                      </Grid> */}

                      <Box className='flex items-center justify-center ml-[20px]'>
                        <DeleteCircle
                          className='w-[16px] h-[16px] cursor-pointer mt-[30px]'
                          onClick={() => handleDeleteCleanCustomField(index)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                );
              }
            )}
          </Grid>
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                className='whitespace-nowrap'
                onClick={addNewCleanCustomField}
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Custom Field
              </Button>
            </Grid>
          </Grid>
          <Divider className='mb-[20px]' />
          <Grid container spacing={3}>
            <DialogTitle className='!text-[15px]'>Heat-Source</DialogTitle>
          </Grid>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>Equipment</InputLabel>
            </Box>
            {['Thermal', 'Clip-On'].map((type: string) => (
              <Box className='w-full flex items-center gap-[8px]' key={type}>
                <Checkbox
                  checked={form.values?.detail?.equipment?.types?.includes(type) || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const currentTypes = form.values?.detail?.equipment?.types || [];
                    const updatedTypes = checked
                      ? [...currentTypes, type]
                      : currentTypes.filter((t: string) => t !== type);

                    form.setFieldValue(`detail.equipment.types`, updatedTypes);
                  }}
                />
                <Box className='text-[14px] text-gray-500'>{type}</Box>
              </Box>
            ))}
          </BaseRow>
          <Divider className='mb-[20px]' />
          {form.values.detail.heatSource?.map((item: any, index: number) => renderHeatSourceField(item, index))}
          <Grid container spacing={3} className='mb-[24px]'>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            <Grid className='flex justify-end' item xs={12} sm={4}>
              <Button
                onClick={addNewHeatSourceItem}
                className='whitespace-nowrap'
                startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
          {
            //   <Divider className='mb-[20px]' />
            // <Grid container spacing={3}>
            //   <DialogTitle>Moisture Points</DialogTitle>
            // </Grid>
            // {/* drybookk */}
            // {(form.values.detail.drybook || []).map((field: any, index: any) => (
            //   <Card className='mb-[20px] p-7' key={index}>
            //     <BaseRow className='gap-[20px] mb-[20px]'>
            //       <Box className='w-full flex justify-end'>
            //         <TrashIcon
            //           className=' w-[20px] h-[20px] cursor-pointer mt-[8px]'
            //           onClick={() => handleDeleteDryBookFields(index)}
            //         />
            //       </Box>
            //     </BaseRow>
            //     <BaseRow className='gap-[20px] mb-[20px]'>
            //       <Box className='w-full'>
            //         <InputLabel>Area</InputLabel>
            //         <BaseSelect
            //           name={`detail.drybook.${index}.area`}
            //           value={cast(form.values?.detail.drybook[index]?.area)}
            //           options={areaScopes}
            //           onChange={(event) => handleAreaChange(event.target.value, index)}
            //         ></BaseSelect>
            //       </Box>
            //       <Box className='w-full'>
            //         <InputLabel>Location</InputLabel>
            //         <BaseSelect
            //           name={`detail.drybook.[${index}].location`}
            //           value={cast(form.values?.detail.drybook?.[index]?.location)}
            //           options={LOCATION}
            //           onChange={(event) => handleLocationChange(event.target.value, index)}
            //         ></BaseSelect>
            //       </Box>
            //     </BaseRow>
            //     <BaseRow className='gap-[20px] mb-[20px]'>
            //       <Box className='w-full'>
            //         <InputLabel>Moisture Point</InputLabel>
            //         <TextField
            //           name={`detail.drybook.[${index}].moisturePoint`}
            //           placeholder='Moisture Point'
            //           value={form.values?.detail?.drybook?.[index]?.moisturePoint ?? ''}
            //           onChange={(e) => {
            //             form.handleChange(e);
            //             const updatedDrybook = updateMoisturePoint(form, index, e.target.value);
            //             form.setFieldValue('detail.drybook', updatedDrybook);
            //           }}
            //           error={Boolean(getIn(form.errors, `detail.drybook.[${index}].moisturePoint`))}
            //           helperText={getIn(form.errors, `detail.drybook.[${index}].moisturePoint`)}
            //           fullWidth
            //         />
            //       </Box>
            //       <Box className='w-full'>
            //         <InputLabel>Dry Standard</InputLabel>
            //         <NumericFormat
            //           customInput={TextField}
            //           name={`detail.drybook.[${index}].dryStandard`}
            //           placeholder='Dry Standard'
            //           value={form.values?.detail?.drybook?.[index]?.dryStandard ?? ''}
            //           onValueChange={(val) => {
            //             const updatedDrybook = updateDrybookGoal(form, index, val.floatValue + '');
            //             form.setFieldValue('detail.drybook', updatedDrybook);
            //           }}
            //           thousandSeparator={true}
            //           fullWidth
            //         />
            //       </Box>
            //     </BaseRow>
            //     <BaseRow className='gap-[20px] mb-[20px]'>
            //       <Box className='w-full'>
            //         <InputLabel>Material Type</InputLabel>
            //         <Autocomplete
            //           freeSolo
            //           options={materialTypes?.map((type: any) => type.name) ?? []} // Extract titles for display
            //           value={form.values?.detail?.drybook?.[index]?.materialType || ''}
            //           onChange={(event, newValue) => {
            //             const updatedReadings = form.values.detail.drybook[index].readings.map((reading: any) => ({
            //               ...reading,
            //               mt: newValue,
            //             }));
            //             form.setFieldValue(`detail.drybook[${index}].materialType`, newValue);
            //             form.setFieldValue(`detail.drybook[${index}].readings`, updatedReadings);
            //           }}
            //           onInputChange={(event, newInputValue) => {
            //             const updatedReadings = form.values.detail.drybook[index].readings.map((reading: any) => ({
            //               ...reading,
            //               mt: newInputValue,
            //             }));
            //             form.setFieldValue(`detail.drybook[${index}].materialType`, newInputValue);
            //             form.setFieldValue(`detail.drybook[${index}].readings`, updatedReadings);
            //           }}
            //           renderInput={(params) => (
            //             <TextField {...params} placeholder='Select or enter a Material Type' fullWidth />
            //           )}
            //         />
            //       </Box>
            //       <Box className='w-full'>
            //         <InputLabel>Goal</InputLabel>
            //         <TextField
            //           className='bg-emerald-100'
            //           name={`detail.drybook.[${index}].goal`}
            //           placeholder='Goal'
            //           value={form.values?.detail?.drybook?.[index]?.goal ?? ''}
            //           onChange={form.handleChange}
            //           error={Boolean(getIn(form.errors, `detail.drybook.[${index}].goal`))}
            //           helperText={getIn(form.errors, `detail.drybook.[${index}].goal`)}
            //           fullWidth
            //           disabled
            //         />
            //       </Box>
            //     </BaseRow>
            //     {(field?.readings || []).map((reading: any, idx: any) => {
            //       const drybookPath = `detail.drybook[${index}].readings[${idx}]`;
            //       return (
            //         <Box key={`${index}-${idx}`}>
            //           <BaseRow className='gap-[20px] mb-[20px]'>
            //             <Box className='w-full'>
            //               {idx === 0 && <InputLabel>Date</InputLabel>}
            //               <BaseDatePicker
            //                 value={form.values?.detail?.drybook?.[index]?.readings?.[idx]?.date ?? ''}
            //                 onChange={(date) => {
            //                   if (date) {
            //                     const selectedDate = dayjs(date).format('MMM DD, YYYY');
            //                     form.setFieldValue(`${drybookPath}.date`, selectedDate);
            //                   }
            //                 }}
            //               />
            //             </Box>
            //             <Box className='w-full'>
            //               {idx === 0 && <InputLabel>MP</InputLabel>}
            //               <TextField
            //                 name={`${drybookPath}.mp`}
            //                 value={form.values?.detail?.drybook?.[index]?.readings?.[idx]?.mp ?? ''}
            //                 onChange={form.handleChange}
            //                 fullWidth
            //               />
            //             </Box>
            //             <Box className='w-full'>
            //               {idx === 0 && <InputLabel>Area</InputLabel>}
            //               <TextField
            //                 name={`${drybookPath}.area`}
            //                 value={form.values?.detail?.drybook?.[index]?.readings?.[idx]?.area ?? ''}
            //                 onChange={form.handleChange}
            //                 fullWidth
            //               />
            //             </Box>
            //             <Box className='w-full'>
            //               {idx === 0 && <InputLabel>MT</InputLabel>}
            //               <TextField
            //                 name={`${drybookPath}.mt`}
            //                 value={form.values?.detail?.drybook?.[index]?.readings?.[idx]?.mt ?? ''}
            //                 onChange={form.handleChange}
            //                 fullWidth
            //               />
            //             </Box>
            //             <Box className='w-full'>
            //               {idx === 0 && <InputLabel>Location</InputLabel>}
            //               <TextField
            //                 name={`${drybookPath}.location`}
            //                 value={form.values?.detail?.drybook?.[index]?.readings?.[idx]?.location ?? ''}
            //                 onChange={form.handleChange}
            //                 fullWidth
            //               />
            //             </Box>
            //             <Box className='w-full'>
            //               {idx === 0 && <InputLabel>Value</InputLabel>}
            //               <TextField
            //                 name={`${drybookPath}.value`}
            //                 placeholder='Value'
            //                 value={form.values?.detail?.drybook?.[index]?.readings?.[idx]?.value ?? ''}
            //                 onChange={form.handleChange}
            //                 fullWidth
            //               />
            //             </Box>
            //             <Box className='w-full'>
            //               {idx === 0 && <InputLabel>Goal</InputLabel>}
            //               <TextField
            //                 name={`${drybookPath}.goal`}
            //                 value={form.values?.detail?.drybook?.[index]?.readings?.[idx]?.goal ?? ''}
            //                 onChange={form.handleChange}
            //                 fullWidth
            //               />
            //             </Box>
            //             <Box className='w-[5px]'>
            //               {idx === 0 && <Box className='w-[5px] mt-[30px]'> </Box>}
            //               <DeleteCircle
            //                 className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
            //                 onClick={() => handleDeleteDryBookItem(idx, index)}
            //               />
            //             </Box>
            //           </BaseRow>
            //           {idx === field.readings.length - 1 && (
            //             <Grid container spacing={3} className='mb-[24px]'>
            //               <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //               <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //               <Grid className='flex justify-end' item xs={12} sm={4}>
            //                 <Button
            //                   onClick={() => addNewDryBookItem(index)}
            //                   className='whitespace-nowrap'
            //                   startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            //                 >
            //                   Add Item
            //                 </Button>
            //               </Grid>
            //             </Grid>
            //           )}
            //         </Box>
            //       );
            //     })}
            //   </Card>
            // ))}
            //   <Grid container spacing={3} className='mb-[24px]'>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}>
            //     <Button
            //       onClick={addNewDryBookField}
            //       className='whitespace-nowrap'
            //       startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            //     >
            //       Add
            //     </Button>
            //   </Grid>
            // </Grid>
            // <Divider className='mb-[20px]' />
          }
          {/* <Grid container spacing={3} mb='10px'>
            <DialogTitle>{t('atmosphericReadings')}</DialogTitle>
          </Grid> */}
          {
            // <Grid container spacing={3}>
            //   <DialogTitle className='!text-[15px]'>Outside</DialogTitle>
            // </Grid>
            // {form.values.detail.atmosphericReadings?.outside?.length > 0 && (
            //   <BaseRow className='gap-[20px] mb-[20px]'>
            //     <Box className='w-full'>
            //       <InputLabel>Date</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>Time</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>Temp</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>RH</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>GPP</InputLabel>
            //     </Box>
            //     <Box className='w-[5px]'> </Box>
            //   </BaseRow>
            // )}
            // {form.values.detail.atmosphericReadings?.outside?.map((item: any, index: number) =>
            //   renderOutsideField(item, index)
            // )}
            // <Grid container spacing={3} className='mb-[24px]'>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}>
            //     <Button
            //       onClick={addNewOutsideItem}
            //       className='whitespace-nowrap'
            //       startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            //     >
            //       Add Item
            //     </Button>
            //   </Grid>
            // </Grid>
            // {/* Unaffected */}
            // <Grid container spacing={3}>
            //   <DialogTitle className='!text-[15px]'>Unaffected</DialogTitle>
            // </Grid>
            // {form.values.detail.atmosphericReadings?.unaffected?.length > 0 && (
            //   <BaseRow className='gap-[20px] mb-[20px]'>
            //     <Box className='w-full'>
            //       <InputLabel>Date</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>Time</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>Temp</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>RH</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>GPP</InputLabel>
            //     </Box>
            //     <Box className='w-[5px]'> </Box>
            //   </BaseRow>
            // )}
            // {form.values.detail.atmosphericReadings?.unaffected?.map((item: any, index: number) =>
            //   renderUnaffectedField(item, index)
            // )}
            // <Grid container spacing={3} className='mb-[24px]'>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}>
            //     <Button
            //       onClick={addNewUnaffectedItem}
            //       className='whitespace-nowrap'
            //       startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            //     >
            //       Add Item
            //     </Button>
            //   </Grid>
            // </Grid>
            // <Grid container spacing={3}>
            //   <DialogTitle className='!text-[15px]'>HVAC</DialogTitle>
            // </Grid>
            // {form.values.detail.atmosphericReadings?.hvac?.length > 0 && (
            //   <BaseRow className='gap-[20px] mb-[20px]'>
            //     <Box className='w-full'>
            //       <InputLabel>Date</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>Time</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>Temp</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>RH</InputLabel>
            //     </Box>
            //     <Box className='w-full'>
            //       <InputLabel>GPP</InputLabel>
            //     </Box>
            //     <Box className='w-[5px]'> </Box>
            //   </BaseRow>
            // )}
            // {form.values.detail.atmosphericReadings?.hvac?.map((item: any, index: number) =>
            //   renderHvacField(item, index)
            // )}
            // <Grid container spacing={3} className='mb-[24px]'>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
            //   <Grid className='flex justify-end' item xs={12} sm={4}>
            //     <Button
            //       onClick={addNewHvacItem}
            //       className='whitespace-nowrap'
            //       startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
            //     >
            //       Add Item
            //     </Button>
            //   </Grid>
            // </Grid>
          }
          {/* <Grid container spacing={3}>
            <DialogTitle className='!text-[15px]'>Affected</DialogTitle>
          </Grid>
          {(form.values.detail.atmosphericReadings?.affected || []).map((field: any, index: any) => (
            <Box key={index}>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full flex justify-center'>
                  <DialogTitle className='!text-[15px]'>{field?.roomName || 'Unnamed Room'}</DialogTitle>
                </Box>
                <Box className='w-[5px]'>
                  <TrashIcon
                    className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
                    onClick={() => handleDeleteAffectedRoom(index)}
                  />
                </Box>
              </BaseRow>

              {(field?.readings || []).map((reading: any, idx: any) => {
                const affectedPath = `detail.atmosphericReadings.affected[${index}].readings[${idx}]`;

                return (
                  <Box key={`${index}-${idx}`}>
                    <BaseRow className='gap-[20px] mb-[20px]'>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>Date</InputLabel>}
                        <BaseDatePicker
                          placeholder='Select Date'
                          value={
                            form.values?.detail?.atmosphericReadings?.affected?.[index]?.readings?.[idx]?.date
                              ? dayjs(
                                  form.values.detail.atmosphericReadings.affected[index].readings[idx].date
                                ).toDate()
                              : undefined
                          }
                          onChange={(date) => {
                            if (date) {
                              const selectedDate = dayjs(date).format('MMM DD, YYYY');
                              form.setFieldValue(`${affectedPath}.date`, selectedDate);
                            }
                          }}
                          error={Boolean(getIn(form.errors, `${affectedPath}.date`))}
                          helperText={getIn(form.errors, `${affectedPath}.date`)}
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>Time</InputLabel>}
                        <BaseTimePicker
                          placeholder='Select Time'
                          value={
                            form.values?.detail?.atmosphericReadings?.affected?.[index]?.readings?.[idx]?.time
                              ? dayjs(
                                  form.values.detail.atmosphericReadings.affected[index].readings[idx].time,
                                  'HH:mm'
                                ).toDate()
                              : undefined
                          }
                          onChange={(time) => {
                            const startTime = dayjs(time).format('HH:mm');
                            form.setFieldValue(`${affectedPath}.time`, startTime);
                          }}
                          error={Boolean(getIn(form.errors, `${affectedPath}.time`))}
                          helperText={getIn(form.errors, `${affectedPath}.time`)}
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>Temp</InputLabel>}
                        <TextField
                          name={`${affectedPath}.temp`}
                          placeholder='Temp'
                          value={
                            form.values?.detail?.atmosphericReadings?.affected?.[index]?.readings?.[idx]?.temp ?? ''
                          }
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, `${affectedPath}.temp`))}
                          helperText={getIn(form.errors, `${affectedPath}.temp`)}
                          fullWidth
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>RH</InputLabel>}
                        <TextField
                          name={`${affectedPath}.rh`}
                          placeholder='RH'
                          value={form.values?.detail?.atmosphericReadings?.affected?.[index]?.readings?.[idx]?.rh ?? ''}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, `${affectedPath}.rh`))}
                          helperText={getIn(form.errors, `${affectedPath}.rh`)}
                          fullWidth
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>GPP</InputLabel>}
                        <TextField
                          name={`${affectedPath}.gpp`}
                          placeholder='GPP'
                          value={
                            form.values?.detail?.atmosphericReadings?.affected?.[index]?.readings?.[idx]?.gpp ?? ''
                          }
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, `${affectedPath}.gpp`))}
                          helperText={getIn(form.errors, `${affectedPath}.gpp`)}
                          fullWidth
                        />
                      </Box>
                      <Box className='w-[5px]'>
                        {idx === 0 && <Box className='w-[5px] mt-[30px]'> </Box>}
                        <DeleteCircle
                          className='w-[16px] h-[16px] cursor-pointer mt-[8px]'
                          onClick={() => handleDeleteAffected(idx, index)}
                        />
                      </Box>
                    </BaseRow>
                    {idx === field.readings.length - 1 && (
                      <Grid container spacing={3} className='mb-[24px]'>
                        <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                        <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                        <Grid className='flex justify-end' item xs={12} sm={4}>
                          <Button
                            onClick={() => addNewAffectedItem(index)}
                            className='whitespace-nowrap'
                            startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
                          >
                            Add Item
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))} */}
          {/* <Grid container spacing={3}>
            <DialogTitle className='!text-[15px]'>DH</DialogTitle>
          </Grid> */}
          {/* {(form.values.detail.atmosphericReadings?.dh || []).map((field: any, index: any) => (
            <Box key={index}>
              <BaseRow className='gap-[20px] mb-[20px]'>
                <Box className='w-full flex justify-center'>
                  <DialogTitle className='!text-[15px]'>{field?.roomName || 'Unnamed Room'}</DialogTitle>
                </Box>
                <Box className='w-[5px]'>
                  <TrashIcon
                    className='w-[20px] h-[20px] cursor-pointer mt-[8px]'
                    onClick={() => handleDeleteDhRoom(index)}
                  />
                </Box>
              </BaseRow>

              {(field?.readings || []).map((reading: any, idx: any) => {
                const dhPath = `detail.atmosphericReadings.dh[${index}].readings[${idx}]`;

                return (
                  <Box key={`${index}-${idx}`}>
                    <BaseRow className='gap-[20px] mb-[20px]'>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>Date</InputLabel>}
                        <BaseDatePicker
                          placeholder='Select Date'
                          value={
                            form.values?.detail?.atmosphericReadings?.dh?.[index]?.readings?.[idx]?.date
                              ? dayjs(form.values.detail.atmosphericReadings.dh[index].readings[idx].date).toDate()
                              : undefined
                          }
                          onChange={(date) => {
                            if (date) {
                              const selectedDate = dayjs(date).format('MMM DD, YYYY');
                              form.setFieldValue(`${dhPath}.date`, selectedDate);
                            }
                          }}
                          error={Boolean(getIn(form.errors, `${dhPath}.date`))}
                          helperText={getIn(form.errors, `${dhPath}.date`)}
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>Time</InputLabel>}
                        <BaseTimePicker
                          placeholder='Select Time'
                          value={
                            form.values?.detail?.atmosphericReadings?.dh?.[index]?.readings?.[idx]?.time
                              ? dayjs(
                                  form.values.detail.atmosphericReadings.dh[index].readings[idx].time,
                                  'HH:mm'
                                ).toDate()
                              : undefined
                          }
                          onChange={(time) => {
                            const startTime = dayjs(time).format('HH:mm');
                            form.setFieldValue(`${dhPath}.time`, startTime);
                          }}
                          error={Boolean(getIn(form.errors, `${dhPath}.time`))}
                          helperText={getIn(form.errors, `${dhPath}.time`)}
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>Temp</InputLabel>}
                        <TextField
                          name={`${dhPath}.temp`}
                          placeholder='Temp'
                          value={form.values?.detail?.atmosphericReadings?.dh?.[index]?.readings?.[idx]?.temp ?? ''}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, `${dhPath}.temp`))}
                          helperText={getIn(form.errors, `${dhPath}.temp`)}
                          fullWidth
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>RH</InputLabel>}
                        <TextField
                          name={`${dhPath}.rh`}
                          placeholder='RH'
                          value={form.values?.detail?.atmosphericReadings?.dh?.[index]?.readings?.[idx]?.rh ?? ''}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, `${dhPath}.rh`))}
                          helperText={getIn(form.errors, `${dhPath}.rh`)}
                          fullWidth
                        />
                      </Box>
                      <Box className='w-full'>
                        {idx === 0 && <InputLabel>GPP</InputLabel>}
                        <TextField
                          name={`${dhPath}.gpp`}
                          placeholder='GPP'
                          value={form.values?.detail?.atmosphericReadings?.dh?.[index]?.readings?.[idx]?.gpp ?? ''}
                          onChange={form.handleChange}
                          error={Boolean(getIn(form.errors, `${dhPath}.gpp`))}
                          helperText={getIn(form.errors, `${dhPath}.gpp`)}
                          fullWidth
                        />
                      </Box>
                      <Box className='w-[5px]'>
                        {idx === 0 && <Box className='w-[5px] mt-[30px]'> </Box>}
                        <DeleteCircle
                          className='w-[16px] h-[16px] cursor-pointer mt-[8px]'
                          onClick={() => handleDeleteDh(idx, index)}
                        />
                      </Box>
                    </BaseRow>
                    {idx === field.readings.length - 1 && (
                      <Grid container spacing={3} className='mb-[24px]'>
                        <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                        <Grid className='flex justify-end' item xs={12} sm={4}></Grid>
                        <Grid className='flex justify-end' item xs={12} sm={4}>
                          <Button
                            onClick={() => addNewDhItem(index)}
                            className='whitespace-nowrap'
                            startIcon={<PlusIcon className='w-[10px] h-[10px] [&>path]:stroke-[#007AFC]' />}
                          >
                            Add Item
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))} */}
          {/* <BaseRow className='flex justify-center gap-[20px] mb-[20px]'>
            <Box>
              <Button color='primary' variant='contained' onClick={() => handleAddAffected()}>
                Add Affected
              </Button>
            </Box>
            <Box>
              <Button color='primary' variant='contained' onClick={() => handleAddDh()}>
                Add DH
              </Button>
            </Box>
          </BaseRow> */}
        </Box>
      </DialogContent>
      <DialogActions>
        {permissions[PERMISSIONS.projectAreaScope_delete] && (
          <Button
            className={`visibility: ${areaId !== 'create' ? 'visible' : 'hidden'}`}
            onClick={() => handleDelete()}
            variant='text'
            color='error'
            disableElevation
            startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='red' />}
          >
            {t('delete', { ns: 'admin/common' })}
          </Button>
        )}
        <Button color='secondary' variant='contained' onClick={() => handleSetOpen()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button color='primary' variant='contained' onClick={() => form.handleSubmit()}>
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescriptionDeleteAreaScope')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelected}
      />
      <ModalAddRoom
        open={openAddRoom}
        setOpen={handleOpenAddRoom}
        addAffectedField={handleSelectRoom}
        currentScreen='areaScope'
        type={modalType}
        roomArea={roomArea}
      />
    </Box>
  );
};

export default AreaScopeDetail;
